import { useEffect, useState } from 'react'
import { errorMessage } from '~/api/baseQuery'
import {
  useCheckClassifierCreditMutation,
  useGetClassifierStatusQuery,
} from '~/api/endpoints/classifier'
import { Button } from '~/components/primitives/Button'
import { TONE_TEXT } from '~/lib/stateTone'

/**
 * Shown above every page while Jev is out of credit, and nowhere else (BIBLE §15.6).
 *
 * A held classifier is the one state where work is stopped on purpose rather than failing,
 * and an operator who does not know that will think the console is broken. So the bar says
 * what is paused, that nothing was lost, and where to top up. It renders nothing otherwise,
 * including when no TypeSafe key is configured at all.
 */

/** Half a minute: a top-up is noticed within a minute, and the poll costs one cheap read. */
export const CLASSIFIER_POLL_MS = 30_000

function plural(count: number, one: string, many: string) {
  return count === 1 ? `${count} ${one}` : `${count} ${many}`
}

export function ClassifierCreditBanner() {
  const { data: status, refetch } = useGetClassifierStatusQuery(undefined, {
    pollingInterval: CLASSIFIER_POLL_MS,
  })
  const [checkCredit, { isLoading: checking }] = useCheckClassifierCreditMutation()
  const [note, setNote] = useState<string | null>(null)
  const held = status?.held === true && status.configured

  // A note from an earlier hold must not greet the next one.
  useEffect(() => {
    if (!held) setNote(null)
  }, [held])

  if (!status || !held) return null

  async function resume() {
    setNote(null)
    try {
      await checkCredit().unwrap()
      const fresh = await refetch().unwrap()
      setNote(fresh.held ? 'Still out of credit' : null)
    } catch (cause) {
      setNote(errorMessage(cause, 'Could not check'))
    }
  }

  const runs = status.paused_runs
  const replies = status.deferred_replies
  const subject =
    runs > 0
      ? `Reply sorting and ${plural(runs, 'table run', 'table runs')} are paused`
      : 'Reply sorting is paused'
  const waiting =
    replies > 0 ? `, ${replies === 1 ? '1 reply is' : `${replies} replies are`} waiting` : ''

  return (
    <div
      role="alert"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-fault/50 bg-fault/10 px-4 py-2 text-xs text-text"
    >
      <p className="min-w-0 flex-1">
        <span className={`label ${TONE_TEXT.fault}`}>Jev is out of credit.</span> {subject}
        {waiting}. Nothing has been lost: it all resumes once credit is back. Top up at{' '}
        <a
          href="https://console.typesafe.ai"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-fault"
        >
          console.typesafe.ai
        </a>
        , then
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" variant="danger" busy={checking} onClick={resume}>
          Resume now
        </Button>
        {note ? <span className={TONE_TEXT.fault}>{note}</span> : null}
      </div>
    </div>
  )
}
